import { Timer3dEnv } from './timer3d-env';
import minuteToModelRotation from './minute-to-model-rotation';
import { getMinutes } from '@entities/timer';
import { Action, AnimationScheduler } from './animation-scheduler';

enum InitialAnimation {
	NOT_STARTED,
	STARTED,
	FINISHED,
}

const TIMER_INITIAL_Y_POSITION = -0.14;

function getInterstitialPosition(currentTime: string): number {
	const [minutes, seconds] = currentTime.split(':').map(Number);
	const currentMinutePosition = minuteToModelRotation[minutes];
	const nextMinutePosition = minuteToModelRotation[minutes + 1];

	return (
		currentMinutePosition +
		((nextMinutePosition - currentMinutePosition) / 60) * seconds
	);
}

export class TimerAnimator {
	private scheduler: AnimationScheduler;
	private initialAnimationStep = InitialAnimation.NOT_STARTED;
	private timer3dScene: Timer3dEnv;

	constructor(timer3dScene: Timer3dEnv) {
		this.timer3dScene = timer3dScene;
		this.scheduler = new AnimationScheduler(this.timer3dScene);
		timer3dScene.entireTimer.position.y = TIMER_INITIAL_Y_POSITION;
	}

	playInitialAnimation() {
		if (this.initialAnimationStep === InitialAnimation.NOT_STARTED) {
			this.scheduler.addAction({
				type: 'move-in',
				handler: ({ clock }) => {
					const initialPosition = TIMER_INITIAL_Y_POSITION;
					const needed = 0;
					const nextValue =
						initialPosition +
						((needed - initialPosition) * (clock.getElapsedTime() * 1000)) /
							1000;

					if (nextValue > needed) {
						this.timer3dScene.entireTimer.position.y = needed;
						return true;
					}

					this.timer3dScene.entireTimer.position.y = nextValue;
					return false;
				},
				onDone: () => (this.initialAnimationStep = InitialAnimation.FINISHED),
				timeToExecute: 1500,
			});
			this.initialAnimationStep = InitialAnimation.STARTED;
		}
	}

	playRotationAnimation(isRunning: boolean, windTime: string) {
		const minutes = getMinutes(windTime);
		const initialRotation = this.timer3dScene.getTopHemisphereRotation();
		const neededRotation = isRunning
			? getInterstitialPosition(String(windTime))
			: minuteToModelRotation[minutes];
		const isDecreasing = initialRotation > neededRotation;

		// Abort any in-flight rotation and start a fresh one to the newest target
		this.scheduler.cancelAction('rotation');

		// Adapt animation time to distance for responsiveness
		const distance = Math.abs(neededRotation - initialRotation);
		// Map full rotation (~2π) to ~220ms, tiny moves to ~80ms
		const duration = Math.min(220, Math.max(80, (distance / (Math.PI * 2)) * 800 + 80));

		this.scheduler.addAction({
			timeToExecute: duration,
			customData: { initialPosition: initialRotation, needed: neededRotation },
			handler: (action: Action) => {
				const elapsedTime = action.clock!.getElapsedTime() * 1000;
				const percentOfTimeElapsed = elapsedTime / action.timeToExecute;

				const nextRotation = isDecreasing
					? initialRotation -
						(initialRotation - neededRotation) * percentOfTimeElapsed
					: initialRotation +
						(neededRotation - initialRotation) * percentOfTimeElapsed;

				if (isDecreasing && nextRotation < neededRotation) {
					this.timer3dScene.rotateTopHemisphere(neededRotation);
					return true;
				} else if (!isDecreasing && nextRotation > neededRotation) {
					this.timer3dScene.rotateTopHemisphere(neededRotation);
					return true;
				}
				this.timer3dScene.rotateTopHemisphere(nextRotation);
				return false;
			},
			type: 'rotation',
		});
	}
}
